/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { ScanResult } from "./types";

type Props = {
  result: ScanResult;
  pages: string[];
};

const formatDay = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString() : "—");

// What the AI read, next to the document, before it opens in an editor.
export const ScanReview = defineComponent<Props>({
  name: "ScanReview",
  props: ["result", "pages"],
  setup(props) {
    const { t } = useLocalization();
    const page = ref(0);

    const fact = (label: string, value: unknown) => (
      <div>
        <dt class="text-[11px] uppercase tracking-wide text-base-content/50">{label}</dt>
        <dd class="font-medium">{value ?? "—"}</dd>
      </div>
    );

    return () => {
      const { document: d, match } = props.result;
      const matched = match.lines.filter((l) => l.stock_item_id).length;
      const total = match.lines.length;
      return (
        <div class="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,18rem)_1fr]">
          <div class="space-y-2">
            <div class="overflow-hidden rounded-xl border border-base-300 bg-base-100">
              {props.pages[page.value] && <img src={props.pages[page.value]} class="w-full" alt="" />}
            </div>
            {props.pages.length > 1 && (
              <div class="flex justify-center gap-1">
                {props.pages.map((_, i) => (
                  <button
                    key={i}
                    class={["btn btn-xs", page.value === i ? "btn-primary" : "btn-ghost"].join(" ")}
                    onClick={() => (page.value = i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div class="min-w-0 space-y-4">
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div class="text-xs uppercase tracking-wide text-base-content/50">{t(`inventory.scan.type_${d.document_type}`)}</div>
                <div class="text-xl font-semibold">{match.supplier_name ?? d.supplier_name ?? t("inventory.scan.unknown_supplier")}</div>
                {match.supplier_created && (
                  <span class="badge badge-info badge-sm mt-1 gap-1">
                    <span class="material-symbols-outlined text-[14px]">add_business</span>
                    {t("inventory.scan.new_supplier")}
                  </span>
                )}
              </div>
              {d.total != null && (
                <div class="text-right">
                  <div class="text-xs text-base-content/50">{t("inventory.invoices.total")}</div>
                  <div class="text-2xl font-semibold tabular-nums">
                    {d.currency && <span class="mr-1 text-sm text-base-content/60">{d.currency}</span>}
                    {d.total.toFixed(2)}
                  </div>
                </div>
              )}
            </div>

            <dl class="grid grid-cols-2 gap-3 rounded-lg bg-base-200 p-3 text-sm sm:grid-cols-4">
              {fact(t("inventory.scan.document_number"), d.document_number)}
              {fact(t("inventory.invoices.issue_date"), formatDay(d.issue_date))}
              {fact(t("inventory.invoices.due_date"), formatDay(d.due_date))}
              {fact(t("inventory.scan.delivery_date"), formatDay(d.delivery_date))}
            </dl>

            <div class="flex flex-wrap gap-2 text-sm">
              <span class={["badge gap-1", matched === total ? "badge-success" : "badge-warning"].join(" ")}>
                <span class="material-symbols-outlined text-[14px]">{matched === total ? "link" : "link_off"}</span>
                {t("inventory.scan.matched").replace("{matched}", String(matched)).replace("{total}", String(total))}
              </span>
              {d.lines_match_total === false && (
                <span class="badge badge-warning gap-1">
                  <span class="material-symbols-outlined text-[14px]">warning</span>
                  {t("inventory.scan.totals_differ")}
                </span>
              )}
            </div>

            <div class="max-h-72 overflow-auto rounded-lg border border-base-300">
              <table class="table table-sm">
                <tbody>
                  {match.lines.map((l, i) => (
                    <tr key={i}>
                      <td class="w-6">
                        <span
                          class={[
                            "material-symbols-outlined text-base",
                            l.stock_item_id ? "text-success" : "text-warning",
                          ].join(" ")}
                          title={l.stock_item_id ? t(`inventory.scan.matched_by_${l.matched_by ?? "name"}`) : t("inventory.scan.needs_mapping")}
                        >
                          {l.stock_item_id ? "check_circle" : "help"}
                        </span>
                      </td>
                      <td>
                        <div class="font-medium">{l.description}</div>
                        {l.supplier_code && <div class="text-xs text-base-content/50">#{l.supplier_code}</div>}
                      </td>
                      <td class="whitespace-nowrap text-right">{`${l.quantity} ${l.unit ?? ""}`}</td>
                      <td class="whitespace-nowrap text-right tabular-nums">{l.unit_price.toFixed(2)}</td>
                      <td class="whitespace-nowrap text-right font-medium tabular-nums">{l.line_total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {matched < total && <p class="text-xs text-base-content/60">{t("inventory.scan.mapping_help")}</p>}
          </div>
        </div>
      );
    };
  },
});
